import type {
  MailAssistantPolicyDraft,
  MailAssistantPolicyLabel,
  MailKnowledgeDocDraft,
  MailKnowledgeDocKind,
  MailKnowledgeScope,
  WizardState,
} from './wizard-store';

export const MAIL_ASSISTANT_AUTO_REPLY_OPTIONS: Array<{
  value: MailAssistantPolicyLabel;
  label: string;
  description: string;
}> = [
  {
    value: 'faq_answerable',
    label: 'FAQ answerable',
    description: 'Question simple qui peut etre repondue a partir de la knowledge base.',
  },
  {
    value: 'out_of_office',
    label: 'Out of office',
    description: "Accuse de reception temporaire, avec rappel programme plutot qu'echange commercial.",
  },
];

export const MAIL_KNOWLEDGE_KIND_OPTIONS: Array<{
  value: MailKnowledgeDocKind;
  label: string;
}> = [
  { value: 'faq', label: 'FAQ' },
  { value: 'objection', label: 'Objection' },
  { value: 'product_note', label: 'Product note' },
  { value: 'example_reply', label: 'Example reply' },
];

export const MAIL_KNOWLEDGE_SCOPE_OPTIONS: Array<{
  value: MailKnowledgeScope;
  label: string;
}> = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'workspace', label: 'Workspace' },
];

function generateDraftId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `draft-${Math.random().toString(36).slice(2, 10)}`;
}

export function createEmptyMailKnowledgeDoc(
  kind: MailKnowledgeDocKind = 'faq',
  scope: MailKnowledgeScope = 'campaign',
): MailKnowledgeDocDraft {
  return {
    id: generateDraftId(),
    scope,
    kind,
    title: '',
    content_md: '',
  };
}

function joinLabels(values: readonly string[]): string {
  return values.join(', ');
}

function renderBulletList(text: string, fallback: string): string {
  const trimmed = text.trim();
  if (!trimmed) return `- ${fallback}`;
  const lines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return `- ${fallback}`;
  return lines
    .map((line) => (line.startsWith('-') ? line : `- ${line}`))
    .join('\n');
}

export function buildMailAssistantPolicyDoc(
  policy: MailAssistantPolicyDraft,
): {
  scope: MailKnowledgeScope;
  kind: 'policy';
  title: string;
  content_md: string;
} {
  return {
    scope: policy.scope,
    kind: 'policy',
    title: 'Assistant reply policy',
    content_md: [
      `auto_reply_enabled: ${policy.auto_reply_enabled ? 'true' : 'false'}`,
      `auto_reply_labels: ${joinLabels(policy.auto_reply_labels)}`,
      '',
      '## Tone',
      policy.tone.trim() || 'Sobre, direct, professionnel et humain.',
      '',
      '## Escalation rules',
      renderBulletList(
        policy.escalation_notes,
        'Escalate to a human when the reply changes commercial scope or stays ambiguous.',
      ),
      '',
      '## Forbidden claims',
      renderBulletList(
        policy.forbidden_claims,
        "Do not invent pricing, delivery timing, technical claims, or promises not approved by ops.",
      ),
      '',
      '## Meeting booking',
      renderBulletList(
        policy.meeting_booking_instructions,
        'If the contact wants a meeting, suggest a short exchange and hand off for final scheduling.',
      ),
      '',
      '## Signature style',
      policy.signature_style.trim() || 'Short closing, no aggressive CTA, no invented social proof.',
    ].join('\n'),
  };
}

export function buildMailAssistantKnowledgePayload(
  store: Pick<WizardState, 'mail_assistant_policy' | 'mail_assistant_docs'>,
): Array<{
  scope: MailKnowledgeScope;
  kind: 'policy' | MailKnowledgeDocKind;
  title: string;
  content_md: string;
}> {
  const docs = store.mail_assistant_docs
    .map((doc) => ({
      scope: doc.scope,
      kind: doc.kind,
      title: doc.title.trim(),
      content_md: doc.content_md.trim(),
    }))
    .filter((doc) => doc.title && doc.content_md);

  return [buildMailAssistantPolicyDoc(store.mail_assistant_policy), ...docs];
}

export function mergeMailAssistantDocs(
  existing: readonly MailKnowledgeDocDraft[],
  incoming: readonly Omit<MailKnowledgeDocDraft, 'id'>[],
): MailKnowledgeDocDraft[] {
  const merged = [...existing];

  for (const next of incoming) {
    const found = merged.find(
      (doc) =>
        doc.kind === next.kind &&
        doc.scope === next.scope &&
        doc.title.trim().toLowerCase() === next.title.trim().toLowerCase(),
    );

    if (found) {
      if (!found.content_md.trim()) {
        found.content_md = next.content_md;
      }
      continue;
    }

    merged.push({
      id: generateDraftId(),
      scope: next.scope,
      kind: next.kind,
      title: next.title,
      content_md: next.content_md,
    });
  }

  return merged;
}
